<script setup lang="ts">
/**
 * Appoint Secretary (비서 선임, B32103) — SG 공통 프리셋 **스튜디오** 패널(문서 재현).
 *   Stage 1 DR (Appoint Secretary) ×1 (현직 이사 서명) · Stage 2 Form 45B ×선임 비서수.
 *   동시 선임/해임(B32111)·해임 단독(B32102)과 같은 cdd 엔진·서명자·DocScope 사용.
 *   e-Filing 은 기존 officer-appointment 화면(Tilko) 그대로 — L_TASK_EFILING_DATA 는 기존 패널과 같은 seed 형식으로 저장.
 *   기존 TASK_DATA 키(officerNameCd 등)는 첫 번째 비서 기준으로 함께 저장(레지스터 등 호환).
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import { provideAgmFill, bareKey, scopeOf } from '@/features/startTask/my_preset/sg/aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '@/features/startTask/my_preset/sg/aura/agm/cdd/agmSignersContext';
import { buildDirectorsSignersJson } from '@/features/startTask/preset_task/sg/components/allotment-shares/composables/allotmentSigners';
import { getActiveDirectors } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { buildAppointedOfficerSignerJson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorSigners';
import { buildSgPresetPrepareDocumentItem } from '@/features/startTask/preset_task/sg/components/presetDocCustSave';
import {
  createEmptyAppointSecretaryForm,
  buildAppointSecretaryTaskFormData,
  buildAppointSecretarySeedFormData,
  syncAppointSecretaryManualContactData,
  type AppointSecretaryFormModel,
} from '@/features/startTask/preset_task/sg/components';
import AppointSecretaryPackageDoc from '@/features/startTask/preset_task/sg/components/appoint-secretary/studio/AppointSecretaryPackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-lookup', context: Record<string, any>): void;
  (e: 'progress-change', pct: number): void;
}>();

const router = useRouter();
const fill = provideAgmFill({ isolatedKeys: [{ scopePrefix: 'f45b_', keys: ['secretary_name'] }] });
const signersCtx = provideAgmSigners();
const creating = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], emitProgress, { immediate: true });

const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const docRef = ref<InstanceType<typeof AppointSecretaryPackageDoc> | null>(null);
const autoLocal = ref<{ newIds: number[] } | null>(null);
const lastAutofillCd = ref('');

const entityCd = computed(() => String(props.entityData?.icpCd || props.entityData?.entityCd || '').trim());
provide(AGM_ENTITY_CD, entityCd);

const st = (v: unknown) => String(v ?? '').trim();
let signerSeq = 0;
function mkSigner(name: string): SignerEntry {
  return { id: `sec-${signerSeq++}`, name: st(name), title: 'Director', role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

// 회사 정보 자동채움 + DR 서명자(현직 이사 전원) 시드.
async function autofill(ent: Record<string, any> | null) {
  if (!ent) return;
  const cd = entityCd.value;
  if (cd && cd === lastAutofillCd.value) return;
  lastAutofillCd.value = cd;
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(k, s); };
  set('company_name', ent.companyName || ent.entityName || ent.ENTITY_NAME);
  set('company_registration_no', ent.registrationNo || ent.regNo || ent.REG_NO);
  if (cd && !st(fill.values.company_registration_no)) {
    try {
      const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
      const src: any = res?.data?.data ?? res?.data ?? res ?? {};
      set('company_registration_no', src.registrationNo);
    } catch { /* ignore */ }
  }
  await seedDirectorSigners(cd);
}

async function seedDirectorSigners(cd: string) {
  if (!cd || signersCtx.signersOf('dr_Signers').length) return;
  try {
    const res: any = await getActiveDirectors(cd);
    const rows: any[] = res?.data?.directors || [];
    const names: string[] = Array.from(new Set(rows.map((d: any) => st(d?.name || d?.contactName)).filter(Boolean)));
    if (names.length && !signersCtx.signersOf('dr_Signers').length) signersCtx.setSigners('dr_Signers', names.map(mkSigner));
  } catch { /* ignore */ }
}

// 선임 비서 이름(✨) → 글로벌 CONTACT 에서 선택.
fill.setActivateHandler((key: string) => {
  if (bareKey(key) !== 'secretary_name') return;
  emit('request-person-lookup', { fieldGroup: 'appointedSecretary', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'New secretary (contact)' });
});
function applyPersonToActive(person: Record<string, any>) {
  const key = fill.activeKey.value; if (!key) return;
  const sc = scopeOf(key);
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(k, s); };
  set(`${sc}secretary_name`, person.name || person.contactName || person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim());
  set(`${sc}secretary_identification_no`, person.identificationNo || person.contactRegNo || person.nricNo || person.idNo);
  set(`${sc}secretary_nationality`, person.nationalityName || person.nationality);
  set(`${sc}secretary_id_type`, person.idTypeName || person.idTypeCd || person.idType || person.identificationType);
  set(`${sc}secretary_address`, person.residentialAddress || person.address || person.primaryAddress);
  set(`${sc}secretary_alternate_address`, person.alternateAddress || person.contactAddress || person.serviceAddress);
  set(`${sc}secretary_birth_date`, person.dateOfBirth || person.birthDate || person.dob);
  set(`${sc}secretary_email`, person.email || person.emailAddress || person.contactEmail);
  set(`${sc}secretary_mobile_no`, person.mobileNo || person.contactNo || person.mobile || person.phoneNo || person.mobileNumber);
  set(`${sc}secretary_country_cd`, person.countryCd || person.mobileCountryCd || person.countryCode);
  set(`${sc}secretary_area_code`, person.areaCode || person.mobileAreaCode);
  set(`${sc}secretary_contact_cd`, person.contactCd || person.CONTACT_CD || person.contactCode);
}
watch(() => props.selectedPerson, (p) => { if (p) applyPersonToActive(p as Record<string, any>); });

// 주소(📍) → 챗봇 주소 입력(appointSecretaryFlow 의 secretaryAddress 핸들러). 돌아온 값은 해당 스코프 키에 반영.
fill.setAddressHandler((key: string) => {
  emit('request-address-lookup', { fieldGroup: 'secretaryAddress', scopedKey: key, addresses: [], directInput: true, subjectLabel: 'Secretary address' });
});
watch(() => props.selectedAddress, (sel) => {
  if (!sel?.value) return;
  const key = st(props.activeAddressContext?.scopedKey) || fill.activeKey.value;
  if (key) fill.setValue(key, sel.value);
});
watch(() => props.entityData, (ent) => { if (!editMode.value) void autofill(ent); }, { immediate: true, deep: true });

onMounted(async () => {
  try { const c = props.selection.task.TASK_CODE; if (c) { const cfg = await getTaskConfigByCode(c); taskConfigSteps.value = cfg?.processSteps || []; } } catch { /* ignore */ }
});

// ── Edit 지원 ──
function gatherState(): Record<string, any> {
  return { values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), local: docRef.value?.gatherLocalState?.() };
}
function restoreState(s: any): void {
  if (!s) return;
  // 이전(비스튜디오) 패널로 만든 task: { formModel: AppointSecretaryFormModel } → 선임 비서 1명(f45b_1).
  if (s.formModel && typeof s.formModel === 'object' && !s.values) {
    const m = s.formModel as AppointSecretaryFormModel;
    const set = (k: string, val: unknown) => { const v = st(val); if (v) fill.setValue(k, v); };
    set('company_name', m.companyName);
    set('company_registration_no', m.companyRegistrationNo);
    set('resolution_date', m.resolutionDate);
    set('f45b_1/appointment_effective_date', m.appointmentEffectiveDate);
    set('f45b_1/execution_date', m.executionDate);
    set('f45b_1/secretary_name', m.secretaryName || m.contactData?.contactName);
    set('f45b_1/secretary_identification_no', m.secretaryIdentificationNo || m.contactData?.contactRegNo);
    set('f45b_1/secretary_address', m.secretaryAddress);
    set('f45b_1/secretary_alternate_address', m.secretaryAlternateAddress);
    set('f45b_1/secretary_nationality', m.secretaryNationality);
    set('f45b_1/secretary_birth_date', m.secretaryDateOfBirth);
    set('f45b_1/secretary_email', m.secretaryEmail);
    set('f45b_1/secretary_mobile_no', m.secretaryContactNo);
    set('f45b_1/secretary_id_type', m.secretaryIdTypeCd);
    set('f45b_1/secretary_country_cd', m.secretaryMobileCountryCd);
    set('f45b_1/secretary_area_code', m.secretaryMobileAreaCode);
    set('f45b_1/secretary_contact_cd', m.contactCd);
    autoLocal.value = { newIds: [1] };
  }
  if (s.values) for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) { autoLocal.value = s.local; docRef.value?.restoreLocalState?.(s.local); }
  void seedDirectorSigners(entityCd.value);
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });

// 스코프(f45b_{id}/) 값 → 문서 formData. 회사키(company_*)는 최상위 값 상속.
function scopedFormData(scope: string): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) if (!k.includes('/')) out[k] = v;
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scope)) continue;
    const bk = bareKey(k); const sv = st(v);
    if (sv === '' && bk in out && st(out[bk]) !== '') continue;
    out[bk] = v;
  }
  return out;
}

const isDrDoc = (n: string) => /DR\s*\(\s*Appoint\s+Secretary/i.test(String(n || ''));
const isForm45BDoc = (n: string) => /Form\s*45\s*B/i.test(String(n || ''));

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) { progress.value = 'Error: enter a Task Title in the chat first.'; return; }
  if (!props.entityData) { progress.value = 'Error: select an entity in the chat first.'; return; }
  const local = docRef.value?.gatherLocalState?.() || { newIds: [] };
  const v = fill.exportPayload();
  const news: number[] = (local.newIds || []).filter((id: number) =>
    !!st(v[`f45b_${id}/secretary_name`]) || !!st(v[`f45b_${id}/secretary_identification_no`]));
  if (!news.length) { progress.value = 'Error: add at least one secretary to appoint.'; return; }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task code missing on preset.'; return; }

  creating.value = true;
  progress.value = 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      // 기존 패널과 동일 — 선임/해임 코드는 e-Filing 단계 강제.
      proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling') || ['B32101', 'B32102', 'B32103', 'B32104'].includes(String(taskTypeCd).trim()),
    };
    const companyBase = {
      company_name: st(v.company_name),
      company_registration_no: st(v.company_registration_no),
      resolution_date: st(v.resolution_date),
    };
    const rows = news.map((id) => ({ id, fd: scopedFormData(`f45b_${id}/`) }));
    const newSecretariesList = rows.map(({ fd }) => ({ secretary_name: st(fd.secretary_name), secretary_identification_no: st(fd.secretary_identification_no) }));

    // 기존 TASK_DATA 형식 호환 — 첫 번째 비서 기준 모델.
    const f = rows[0].fd;
    const model: AppointSecretaryFormModel = {
      ...createEmptyAppointSecretaryForm(),
      companyName: companyBase.company_name,
      companyRegistrationNo: companyBase.company_registration_no,
      resolutionDate: companyBase.resolution_date,
      appointmentEffectiveDate: st(f.appointment_effective_date),
      executionDate: st(f.execution_date),
      secretaryName: st(f.secretary_name),
      secretaryIdentificationNo: st(f.secretary_identification_no),
      secretaryAddress: st(f.secretary_address),
      secretaryAlternateAddress: st(f.secretary_alternate_address),
      secretaryNationality: st(f.secretary_nationality),
      secretaryDateOfBirth: st(f.secretary_birth_date),
      secretaryEmail: st(f.secretary_email),
      secretaryContactNo: st(f.secretary_mobile_no),
      secretaryIdTypeCd: st(f.secretary_id_type),
      secretaryMobileCountryCd: st(f.secretary_country_cd),
      secretaryMobileAreaCode: st(f.secretary_area_code),
      contactCd: st(f.secretary_contact_cd),
    };
    syncAppointSecretaryManualContactData(model);
    const taskFormData: Record<string, any> = {
      ...buildAppointSecretaryTaskFormData(model, entityCd.value || ''),
      ...companyBase,
      newSecretaries: rows.map(({ fd }) => ({
        contactCd: st(fd.secretary_contact_cd), name: st(fd.secretary_name), identificationNo: st(fd.secretary_identification_no),
        appointmentDate: st(fd.appointment_effective_date),
      })),
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskData: { formData: taskFormData } }) });
      try { await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskTitle: props.taskTitle }) }); } catch { /* optional */ }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ entityCd: entityCd.value || null, taskTypeCd, taskTypeName: props.selection.task.TASK_NAME, taskTitle: title, taskFlow, officerData: taskFormData }) });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) { progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`; creating.value = false; return; }
    }

    progress.value = 'Saving documents...';
    // L_TASK_EFILING_DATA 초기 저장 — 기존 패널과 동일한 e-Filing seed 형식(첫 번째 비서 기준, Tilko officer-appointment 화면 호환).
    try {
      await saveEFilingData(taskId, {
        taskTypeCd,
        entityCd: entityCd.value || null,
        countryCd: 'SG',
        formData: buildAppointSecretarySeedFormData(model, entityCd.value || '') as Record<string, unknown>,
        requestPayload: null,
        responseData: null,
        status: 'B36002',
        isSuccess: false,
      });
    } catch (e) { console.warn('[AppointSecretary] L_TASK_EFILING_DATA initial save failed', e); }

    const drSigners = signersCtx.signersOf('dr_Signers');
    const drSignersJson = drSigners.length ? JSON.stringify(drSigners) : (await buildDirectorsSignersJson(entityCd.value || ''));
    const docsToSave: Array<Record<string, any>> = [];
    for (const d of (props.selection.documents || [])) {
      const tid = Number(d?.tmpId || 0); if (!tid) continue;
      const name = String(d.templateName || d.docCode || '');
      if (isDrDoc(name)) {
        const drFd: Record<string, any> = { ...companyBase, new_secretaries: newSecretariesList, signers: drSignersJson, Signers: drSignersJson };
        docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: name, personName: companyBase.company_name || 'Company', commonFormData: drFd, entityCd: entityCd.value || undefined }));
      } else if (isForm45BDoc(name)) {
        rows.forEach(({ id, fd }) => {
          const pn = st(fd.secretary_name);
          const sj = buildAppointedOfficerSignerJson({ name: pn, email: st(fd.secretary_email), role: 'secretary', contactCd: st(fd.secretary_contact_cd), contactRegNo: st(fd.secretary_identification_no) });
          const docFd = { ...fd, signers: sj, Signers: sj };
          const label = rows.length > 1 ? `${name} — ${pn || `Secretary ${id}`}` : name;
          docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: label, personName: pn, commonFormData: docFd, entityCd: entityCd.value || undefined }));
        });
      }
    }

    if (!docsToSave.length) {
      const templateNames = (props.selection.documents || []).map((d: any) => String(d?.templateName || d?.docCode || ''));
      console.warn('[AppointSecretary] docsToSave EMPTY', { presetDocs: templateNames, newsCount: news.length });
      progress.value = `Warning: task created but no documents matched. templates=${templateNames.join(', ') || '(none)'}`;
    } else {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((x) => x.json());
        if (!r?.success) progress.value = `Documents save failed: ${r?.message || 'unknown'}`;
      } catch (err) {
        progress.value = `Documents save failed: ${err instanceof Error ? err.message : String(err)}`;
      }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    progress.value = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally { creating.value = false; }
}
</script>

<template>
  <div class="sec-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Appoint Secretary' }}</div>
        <div class="co-subtitle">Directors&rsquo; resolution — Appoint secretary (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div class="co-doc"><AppointSecretaryPackageDoc ref="docRef" :auto-local="autoLocal" /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!entityData || !effectiveTaskTitle || creating" @click="handleCreateTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sec-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sec-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
